// keyword.queries.ts
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { fetchPosts } from "./keyword.api";
import type { KeywordState } from "../types/keyword.types";
import type { KeywordPostQueryParams } from "../types/keyword.types";
import { useKeywordApiToken } from "@/features/auth/hooks/useApiToken";
import { useKeywordsStore } from "@/stores/keywordsStore";
import { useQueryErrorToast } from "@/hooks/useQueryErrorToast";

export const useKeywordPostsQuery = (
  state: KeywordState,
  isEnabled: boolean,
) => {
  const { apiToken } = useKeywordApiToken();
  const queryClient = useQueryClient();

  const keywords = useKeywordsStore((s) => s.keywords);

  const queryParams: KeywordPostQueryParams = {
    "x-api-token": apiToken ?? "",
    include_keywords: keywords.main || [],
    additional_keywords: keywords.additional || [],
    exclude_keywords: keywords.excluded || [],
    limit: state.limit,
    page: 1,
    date_range: state.dateRange,
    platforms: state.selectedPlatforms,
    influencer_id: state.influencerId || undefined,
    is_verified: state.isVerified || undefined,
    subfields: state.selectedSubFields,
  };

  // Exclude auth token from cache key — it's a request concern, not a data identity
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { "x-api-token": _token, ...cacheKey } = queryParams;

  const query = useQuery({
    queryKey: ["keyword-posts", cacheKey],
    queryFn: () => fetchPosts(queryParams),
    enabled: isEnabled,
  });

  useEffect(() => {
    if (query.dataUpdatedAt) {
      queryClient.invalidateQueries({ queryKey: ["api-token"] });
    }
  }, [query.dataUpdatedAt]);

  useQueryErrorToast(query, "Failed to fetch Keyword Posts");

  return query;
};
